import React, { lazy, Suspense } from 'react';
import ReactDOM from 'react-dom/client';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import {
  connectorsForWallets,
  darkTheme,
  getDefaultConfig,
  RainbowKitProvider,
} from '@rainbow-me/rainbowkit';
import { WagmiProvider } from 'wagmi';
import { QueryClientProvider, QueryClient } from '@tanstack/react-query';
import { Provider } from 'react-redux';

import Root from './routes/Root';
import HomePage from './routes/HomePage';
import { chains } from './constants/chain';

import PageLoader from './components/FallbackLoader';
import { SocketProvider } from './socketContext';
import { store } from './store';
import '@rainbow-me/rainbowkit/styles.css';
import './index.css';
import { DynamicThemeProvider } from './DynamicThemeProvider';
import { ModalProvider } from './hooks/useModalContext';
// import reportWebVitals from './reportWebVitals';
import VConsole from 'vconsole';
import {
  metaMaskWallet,
  okxWallet,
  bybitWallet,
  rainbowWallet,
  coinbaseWallet,
  walletConnectWallet,
} from '@rainbow-me/rainbowkit/wallets';
import ErrorPage from './components/Error';
const CreateGamePage = lazy(() => import('./routes/CreateGame'));
const GamePage = lazy(() => import('./routes/Game'));
const GameSettingPage = lazy(() => import('./routes/GameSetting'));
const GameConfig = lazy(() => import('./routes/GameSetting/Game'));
const GamePlayer = lazy(() => import('./routes/GameSetting/Player'));
const GameLedger = lazy(() => import('./routes/GameSetting/Ledger'));

const connectors = connectorsForWallets(
  [
    {
      groupName: 'Recommended',
      wallets: [
        bybitWallet,
        metaMaskWallet,
        okxWallet,
        rainbowWallet,
        coinbaseWallet,
        walletConnectWallet,
      ],
    },
  ],
  {
    appName: 'Variance',
    projectId: process.env.REACT_APP_WALLET_CONNECT_PROJECT_ID as string,
  }
);
const queryClient = new QueryClient();
export const config = getDefaultConfig({
  connectors,
  appName: 'Variance',
  projectId: process.env.REACT_APP_WALLET_CONNECT_PROJECT_ID as string,
  // @ts-expect-error
  chains,
});

const router = createBrowserRouter([
  {
    path: '/',
    element: <Root />,
    errorElement: <ErrorPage />,
    children: [
      {
        path: '/',
        element: <HomePage />,
      },
      {
        path: '/create-game',
        element: (
          <Suspense fallback={<PageLoader title="game table..." />}>
            <CreateGamePage />
          </Suspense>
        ),
      },
      {
        path: '/game/:gameId',
        element: (
          <Suspense fallback={<PageLoader title="game table..." />}>
            <GamePage />
          </Suspense>
        ),
      },
      {
        path: '/game-setting/:gameId',
        element: (
          <Suspense fallback={<PageLoader title="game setting..." />}>
            <GameSettingPage />
          </Suspense>
        ),
        children: [
          {
            path: '/game-setting/:gameId/game',
            element: (
              <Suspense fallback={<PageLoader title="game setting..." />}>
                <GameConfig />
              </Suspense>
            ),
          },
          {
            path: '/game-setting/:gameId/players',
            element: (
              <Suspense fallback={<PageLoader title="game player list..." />}>
                <GamePlayer />
              </Suspense>
            ),
          },
          {
            path: '/game-setting/:gameId/ledger',
            element: (
              <Suspense fallback={<PageLoader title="ledger..." />}>
                <GameLedger />
              </Suspense>
            ),
          },
        ],
      },
    ],
  },
]);
// new VConsole();
const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

root.render(
  <React.StrictMode>
    <WagmiProvider config={config}>
      <QueryClientProvider client={queryClient}>
        <DynamicThemeProvider
          // theme={theme}
          toastOptions={{
            defaultOptions: {
              position: 'top-right',
              duration: 3000,
            },
          }}
        >
          <RainbowKitProvider theme={darkTheme()}>
            <SocketProvider>
              <Provider store={store}>
                <ModalProvider>
                  <RouterProvider router={router} />
                </ModalProvider>
              </Provider>
            </SocketProvider>
          </RainbowKitProvider>
        </DynamicThemeProvider>
      </QueryClientProvider>
    </WagmiProvider>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
// reportWebVitals();
